import { FeatureFlags } from '@/types/featureFlags';
import { getFlag } from '@/plugins/launchdarkly';

let pinnedValue: boolean | undefined;

/**
 * Resolves the granular-permissions flag once, via the consolidated LaunchDarkly client,
 * and freezes it for the session. Call from bootstrapSession() after the LD client has
 * been identified, so a later mid-session variation change (LD streaming) or a team
 * switch (re-identify) cannot alter the pinned value.
 *
 * Pinning — rather than reading live — keeps src/api's version selection and the
 * permissions store's derivation from disagreeing about which grants apply mid-session.
 * Safe because this flag is targeted by org, not team: org never changes mid-session, so
 * the only way to disagree with the pin is a rare LD edit in dev/staging (the flag stays
 * off in production until the project is complete), and that case is resolved by a page
 * reload rather than by this module reacting to the change.
 */
export function pinGranularPermissionsFlag(): void {
  pinnedValue = getFlag(FeatureFlags.GRANULAR_PERMISSIONS, false);
}

/**
 * The single accessor for the pinned granular-permissions value. src/api and the
 * permissions store both read through here so they cannot disagree about which grants
 * apply. Returns false if called before pinGranularPermissionsFlag() has run — defensive
 * only, since bootstrapSession() guarantees pinning completes before any authenticated
 * route renders.
 */
export function isGranularPermissionsEnabled(): boolean {
  return pinnedValue ?? false;
}

/**
 * Clears the pinned value so the next bootstrapSession() re-pins from scratch. Call from
 * logout / local-state reset so a same-tab re-login cannot reuse the previous session's
 * pinned value.
 */
export function resetGranularPermissionsFlag(): void {
  pinnedValue = undefined;
}
